import './MonitorApplication.scss';
import { AppDisplay } from '@ui/applications';
import { Link } from 'react-router-dom';
import { monitorMenuConfigs } from './MonitorMenu';
import { SonarText } from '@ui/text';
import { useDataSecurityMonitors } from '@hooks/data-security/useDataSecurityMonitors';
import { Alert, Button, Form, Select, Space } from 'antd';
import { COPY_SPECIFIC, FULL_ROUTES, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { Else, If, Then, When } from 'react-if';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDataSecurityApps, usePrevious } from '@hooks';
export function getApplicationList(monitors, applications, type) {
    var _a, _b;
    if (type === undefined) {
        return undefined;
    }
    const monitorsByType = monitors.filter((monitor) => { var _a; return ((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) === type; });
    const maxCount = (_b = (_a = monitorMenuConfigs.find((config) => config.type === type)) === null || _a === void 0 ? void 0 : _a.max) !== null && _b !== void 0 ? _b : 1;
    return applications.reduce((result, application) => (monitorsByType
        .filter((monitor) => monitor.applicationId === application.applicationId)
        .length >= maxCount ?
        [...result] :
        [...result, {
                label: application.name,
                value: application.applicationId
            }]), []);
}
export const MonitorApplication = ({ editing, fieldId, hidden = false, monitor, onChange }) => {
    var _a, _b, _c;
    const monitors = useDataSecurityMonitors();
    const applications = useDataSecurityApps();
    const previousMonitor = usePrevious(monitor);
    const applicationOptions = useMemo(() => { var _a; return getApplicationList(monitors, applications, (_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId); }, [monitors, applications, monitor]);
    const defaultApp = useMemo(() => {
        var _a;
        if (monitor.applicationId) {
            return monitor.applicationId;
        }
        const appOptions = getApplicationList(monitors, applications, (_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId);
        if ((previousMonitor === null || previousMonitor === void 0 ? void 0 : previousMonitor.applicationId) && (appOptions === null || appOptions === void 0 ? void 0 : appOptions.find((x) => x.value === previousMonitor.applicationId))) {
            return previousMonitor.applicationId;
        }
        else if ((appOptions === null || appOptions === void 0 ? void 0 : appOptions.length) === 1) {
            return appOptions[0].value;
        }
        return undefined;
    }, [monitor, applications, monitors, previousMonitor]);
    const handleOnChange = useCallback((newApp) => {
        if (onChange) {
            onChange(newApp);
        }
    }, [onChange]);
    useEffect(() => {
        var _a, _b;
        if ((((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) !== ((_b = previousMonitor === null || previousMonitor === void 0 ? void 0 : previousMonitor.monitorConfig) === null || _b === void 0 ? void 0 : _b.monitorTypeId) ||
            monitor.applicationId !== defaultApp) &&
            !monitor.templateId) {
            handleOnChange(defaultApp);
        }
    }, [defaultApp, previousMonitor, handleOnChange, monitor]);
    if (!((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId)) {
        return;
    }
    return (React.createElement(If, { condition: monitor.templateId },
        React.createElement(Then, null,
            React.createElement(SonarText, { className: 'h3' }, COPY_SPECIFIC.IMPACTED_APPLICATIONS),
            React.createElement(Form.Item, { className: 'monitor-application', name: fieldId === undefined ? 'applicationIds' : [fieldId, 'applicationIds'] },
                React.createElement(Space, null,
                    React.createElement(If, { condition: monitor.applicationIds && ((_b = monitor.applicationIds) === null || _b === void 0 ? void 0 : _b.length) > 0 },
                        React.createElement(Then, null, (_c = monitor.applicationIds) === null || _c === void 0 ? void 0 : _c.map((appId) => React.createElement(AppDisplay, { appId: appId, key: appId }))),
                        React.createElement(Else, null,
                            React.createElement(Alert, { message: React.createElement(Space, null,
                                    COPY_SPECIFIC.SETUP_CRITICAL_APPLICATIONS_MESSAGE,
                                    React.createElement(Link, { to: FULL_ROUTES.DATA_SECURITY_APPLICATION_SETUP },
                                        React.createElement(Button, { size: 'small', type: 'primary' }, COPY_SPECIFIC.SETUP_CRITICAL_APPLICATIONS))), showIcon: true, type: 'info' })))))),
        React.createElement(Else, null,
            React.createElement(When, { condition: !hidden },
                React.createElement(SonarText, { className: 'h3' }, COPY_SPECIFIC.SELECT_AN_APP)),
            React.createElement(Form.Item, { className: 'monitor-application', hidden: hidden, initialValue: defaultApp, name: fieldId === undefined ? 'applicationId' : [fieldId, 'applicationId'] },
                React.createElement(Select, { disabled: !editing, onChange: handleOnChange, optionLabelProp: 'label', optionRender: (app) => React.createElement(AppDisplay, { appId: Number(app.value) }), options: applicationOptions, placeholder: PLACEHOLDERS_COPY.SELECT_AN_APPLICATION, showSearch: true })))));
};
